import { ArrowRight, CheckCircle2, Star, Phone, Warehouse, Shield, Clock, ChevronDown, Lock, Thermometer, Camera, Package, Home, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Contact from '../components/Contact';

export default function Zwischenlagerung() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const benefits = [
    'Sichere, überwachte Lagerhallen in Deutschland',
    'Klimatisiert und trocken für Ihre Möbel',
    'Flexible Lagerdauer – Tage, Wochen, Monate',
    'Vollständig versichert während der Lagerzeit',
    'Direkter Transport von der Schweiz ins Lager',
    'Zugang zu Ihren Sachen auf Anfrage möglich',
  ];

  const features = [
    {
      icon: Shield,
      title: 'Rundum geschützt',
      description: 'Ihre Möbel und persönlichen Gegenstände sind in unseren Lagerhallen vollständig versichert. Wir garantieren einen sicheren Schutz vor Beschädigung, Diebstahl und Witterungseinflüssen.',
    },
    {
      icon: Thermometer,
      title: 'Klimatisiert',
      description: 'Unsere Lagerhallen sind klimatisiert und trocken. Ihre Möbel, Holzgegenstände und empfindliche Güter werden optimal gelagert – ohne Schimmel, Feuchtigkeit oder Temperaturschwankungen.',
    },
    {
      icon: Camera,
      title: 'Videoüberwacht',
      description: 'Alle Lagerhallen sind mit moderner Videoüberwachung ausgestattet und 24/7 überwacht. Nur autorisiertes Personal hat Zugang zu den Lagerräumen – Ihre Sachen sind in sicheren Händen.',
    },
    {
      icon: Lock,
      title: 'Abgeschlossen',
      description: 'Jeder Lagerbereich ist separat abgeschlossen. Sie erhalten auf Wunsch Ihren eigenen Schlüssel und können nach Vereinbarung jederzeit auf Ihre eingelagerten Gegenstände zugreifen.',
    },
  ];

  const storageSizes = [
    {
      size: 'Klein (10-15m³)',
      ideal: '1-2 Zimmer Wohnung',
      price: 'ab CHF 180.–/Monat',
      items: [
        'Kleine Wohnung',
        'Teilumzug',
        'Temporäre Lagerung',
        'Einzelne Möbelstücke',
      ],
    },
    {
      size: 'Mittel (20-30m³)',
      ideal: '3-4 Zimmer Wohnung',
      price: 'ab CHF 280.–/Monat',
      popular: true,
      items: [
        'Standard-Haushalt',
        'Kompletter Hausrat',
        'Mehrmonatige Lagerung',
        'Überbrückung Mietlücke',
      ],
    },
    {
      size: 'Gross (40-60m³)',
      ideal: '5-6 Zimmer Haus',
      price: 'ab CHF 420.–/Monat',
      items: [
        'Grosser Haushalt',
        'Einfamilienhaus',
        'Langzeitlagerung',
        'Komplette Einrichtung',
      ],
    },
  ];

  const useCases = [
    {
      icon: Home,
      title: 'Neue Wohnung noch nicht bereit',
      description: 'Ihre alte Wohnung muss geräumt werden, aber die neue ist noch nicht bezugsbereit? Wir lagern Ihren kompletten Hausrat sicher ein, bis Sie einziehen können.',
    },
    {
      icon: Clock,
      title: 'Überbrückung von Mietlücken',
      description: 'Zwischen alter und neuer Wohnung liegt eine zeitliche Lücke? Mit unserer Zwischenlagerung überbrücken Sie problemlos Tage, Wochen oder sogar Monate.',
    },
    {
      icon: Truck,
      title: 'Renovierung oder Umbau',
      description: 'Während Renovierungsarbeiten in Ihrer neuen Wohnung lagern wir Ihre Möbel sicher ein. So sind sie geschützt vor Staub, Farbe und Beschädigungen.',
    },
    {
      icon: Package,
      title: 'Platzmangel',
      description: 'Die neue Wohnung ist kleiner? Lagern Sie überzählige Möbel und Gegenstände ein, bis Sie eine grössere Wohnung gefunden haben oder sie verkaufen.',
    },
  ];

  const whyChooseUs = [
    {
      title: 'Direkt vom Umzug ins Lager',
      description: 'Wir transportieren Ihre Möbel direkt von der Schweiz in unser deutsches Lager – ohne Umwege. Auf Wunsch liefern wir später an Ihre neue Adresse.',
    },
    {
      title: 'Flexible Lagerdauer',
      description: 'Von wenigen Tagen bis mehreren Monaten – Sie entscheiden wie lange. Keine Mindestmietdauer, faire Preise ab dem ersten Tag.',
    },
    {
      title: 'Professionelle Einlagerung',
      description: 'Ihr Umzugsgut wird fachgerecht gestapelt und gelagert. Empfindliche Gegenstände werden besonders geschützt und gekennzeichnet.',
    },
    {
      title: 'Transparente Kosten',
      description: 'Keine versteckten Gebühren. Sie zahlen nur für die tatsächlich genutzte Zeit und den benötigten Lagerplatz.',
    },
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Bedarfsermittlung',
      description: 'Wir berechnen anhand Ihrer Möbel die benötigte Lagerfläche und erstellen ein individuelles Angebot.',
    },
    {
      step: '2',
      title: 'Transport ins Lager',
      description: 'Wir holen Ihr Umzugsgut in der Schweiz ab und transportieren es direkt in unser sicheres Lager in Deutschland.',
    },
    {
      step: '3',
      title: 'Einlagerung',
      description: 'Ihre Möbel werden professionell eingelagert, inventarisiert und in unserem System erfasst.',
    },
    {
      step: '4',
      title: 'Sichere Lagerung',
      description: 'Ihr Hausrat wird in klimatisierten, überwachten Räumen gelagert – so lange Sie möchten.',
    },
    {
      step: '5',
      title: 'Auslieferung',
      description: 'Wenn Ihre neue Wohnung bereit ist, liefern wir Ihre Möbel direkt an die gewünschte Adresse.',
    },
  ];

  const testimonials = [
    {
      name: 'Julia M.',
      location: 'Zürich → München',
      text: 'Unsere Wohnung war 6 Wochen später fertig als geplant. Gott sei Dank konnte ich alles einlagern. Das hat uns sehr geholfen!',
      rating: 5,
    },
    {
      name: 'Thomas W.',
      location: 'Basel',
      text: 'Die Lagerhalle war sauber und trocken. Nach 3 Monaten waren alle Möbel noch in perfektem Zustand. Super Service!',
      rating: 5,
    },
    {
      name: 'Familie Hoffmann',
      location: 'Bern → Frankfurt',
      text: 'Zwischen Auszug und Einzug lagen 2 Monate. Die Zwischenlagerung hat reibungslos funktioniert. Sehr professionell!',
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: 'Was kostet eine Zwischenlagerung bei einem Umzug von der Schweiz nach Deutschland?',
      answer: 'Die Kosten für Zwischenlagerung hängen vom <strong>Volumen</strong> (m³) und der <strong>Lagerdauer</strong> ab:<br><br>• <strong>Klein (10-15m³):</strong> ab CHF 180.–/Monat (1-2 Zimmer)<br>• <strong>Mittel (20-30m³):</strong> ab CHF 280.–/Monat (3-4 Zimmer)<br>• <strong>Gross (40-60m³):</strong> ab CHF 420.–/Monat (5-6 Zimmer)<br><br>Zusätzlich fallen einmalig Kosten für Transport (ins Lager) und Auslieferung (an neue Adresse) an. Bei Buchung eines <Link to="/komplettumzug" className="text-slate-900 font-semibold hover:underline">Komplettumzugs</Link> oder <Link to="/umzug-zuerich-deutschland" className="text-slate-900 font-semibold hover:underline">Umzug Zürich Deutschland</Link> erhalten Sie Sonderkonditionen. Kontaktieren Sie uns für eine individuelle Offerte.'
    },
    {
      question: 'Wie lange kann ich meine Möbel einlagern?',
      answer: 'Sie können Ihre Möbel so lange einlagern wie Sie möchten – von <strong>wenigen Tagen bis zu mehreren Monaten oder Jahren</strong>. Es gibt <strong>keine Mindestmietdauer</strong>. Die Abrechnung erfolgt monatsweise. Kurzzeitlagerungen unter 1 Monat werden anteilig berechnet. Die meisten unserer Kunden lagern zwischen 2 Wochen und 3 Monaten ein.'
    },
    {
      question: 'Wo werden meine Möbel gelagert?',
      answer: 'Wir lagern Ihre Möbel in <strong>modernen, sicheren Lagerhallen in Deutschland</strong> (je nach Zieladresse in der Nähe von München, Stuttgart, Frankfurt oder anderen Standorten). Alle Lagerhallen sind:<br><br>• Klimatisiert und trocken<br>• Videoüberwacht (24/7)<br>• Separat abgeschlossen<br>• Versichert<br><br>Auf Wunsch können Sie die Lagerhalle vorab besichtigen.'
    },
    {
      question: 'Ist mein Umzugsgut während der Lagerung versichert?',
      answer: 'Ja, Ihr gesamter Hausrat ist während der Lagerung <strong>vollständig versichert</strong>. Die Versicherung deckt:<br><br>• Beschädigungen<br>• Diebstahl<br>• Brand und Wasserschäden<br>• Höhere Gewalt<br><br>Standard-Versicherungssumme: bis CHF 100\'000. Bei besonders wertvollen Haushalten können Sie eine höhere Versicherung abschliessen. Die Versicherung ist bereits in den Lagerkosten enthalten.'
    },
    {
      question: 'Kann ich während der Lagerung auf meine Sachen zugreifen?',
      answer: 'Ja, <strong>Zugang ist jederzeit möglich</strong> – nach vorheriger Vereinbarung. Bitte kontaktieren Sie uns mindestens 24 Stunden vorher, damit wir einen Termin koordinieren können. Der Zugang zu Ihren eingelagerten Gegenständen ist <strong>kostenlos</strong>. Falls Sie regelmässigen Zugang benötigen, können wir auch einen eigenen Lagerraum mit individuellem Zugang für Sie einrichten.'
    },
    {
      question: 'Was passiert, wenn meine neue Wohnung später fertig wird als geplant?',
      answer: 'Kein Problem! Die Lagerdauer ist <strong>flexibel verlängerbar</strong>. Informieren Sie uns einfach, dass sich Ihr Einzugstermin verschiebt. Die Verlängerung erfolgt automatisch zum gleichen Monatspreis. Es fallen <strong>keine Zusatzgebühren</strong> oder Stornokosten an. Sie zahlen einfach für die zusätzlichen Tage/Wochen weiter.'
    },
    {
      question: 'Muss ich selbst ein- und auspacken oder macht ihr das?',
      answer: 'Wir übernehmen das <strong>komplette Ein- und Auspacken</strong> für Sie:<br><br><strong>Einlagerung:</strong><br>• Transport von Ihrer alten Wohnung ins Lager<br>• Professionelle Einlagerung und Inventarisierung<br>• Optimale Stapelung für Platzsparung<br><br><strong>Auslieferung:</strong><br>• Transport vom Lager zur neuen Wohnung<br>• Auspacken und Montage der Möbel<br>• Aufstellung am gewünschten Platz<br><br>Sie müssen nichts selbst machen – wir erledigen alles.'
    },
    {
      question: 'Wie werden empfindliche Gegenstände im Lager geschützt?',
      answer: 'Empfindliche Gegenstände erhalten <strong>besonderen Schutz</strong>:<br><br>• Verpackung mit Möbeldecken, Luftpolsterfolie<br>• Separate Lagerung (nicht unter schweren Gegenständen)<br>• Kennzeichnung als "Vorsicht zerbrechlich"<br>• Dokumentation mit Fotos<br><br>Besonders wertvolle Gegenstände (Kunstwerke, Antiquitäten) können in klimatisierten Spezialräumen gelagert werden. Sprechen Sie uns darauf an!'
    },
    {
      question: 'Kann ich nur Teile meines Hausrats einlagern?',
      answer: 'Ja, Sie können <strong>nur bestimmte Möbel und Gegenstände</strong> einlagern. Das ist praktisch wenn:<br><br>• Die neue Wohnung kleiner ist<br>• Sie Platzmangel haben<br>• Einzelne Möbel nicht sofort benötigt werden<br>• Sie aussortieren möchten<br><br>Die Kosten berechnen sich nach dem tatsächlichen Volumen. Auch kleine Mengen ab 5m³ sind möglich.'
    },
    {
      question: 'Was darf nicht eingelagert werden?',
      answer: '<strong>Nicht erlaubt sind:</strong><br><br>• Verderbliche Lebensmittel<br>• Lebende Tiere oder Pflanzen<br>• Gefährliche Stoffe (Chemikalien, Gas, Munition)<br>• Illegale Gegenstände<br>• Drogen und Betäubungsmittel<br><br><strong>Erlaubt sind:</strong><br><br>• Alle Möbel und Haushaltsgeräte<br>• Kleidung, Bücher, Spielzeug<br>• Elektrogeräte (TV, Computer etc.)<br>• Fahräder, Sportgeräte<br>• Verpackte, haltbare Lebensmittel (Konserven)<br><br>Bei Unsicherheiten fragen Sie uns gerne!'
    }
  ];

  const safetyFeatures = [
    {
      title: '24/7 Videoüberwachung',
      description: 'Alle Bereiche werden permanent überwacht',
    },
    {
      title: 'Alarmanlagen',
      description: 'Moderne Einbruchsmeldesysteme',
    },
    {
      title: 'Zugangskontrolle',
      description: 'Nur autorisiertes Personal hat Zutritt',
    },
    {
      title: 'Feuerschutz',
      description: 'Sprinkleranlagen und Rauchmelder',
    },
    {
      title: 'Klimakontrolle',
      description: 'Konstante Temperatur und Luftfeuchtigkeit',
    },
    {
      title: 'Versicherung',
      description: 'Vollständiger Versicherungsschutz inklusive',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-white/20">
              Zwischenlagerung Schweiz → Deutschland
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Sichere Zwischenlagerung für Ihren Umzug
            </h1>

            <p className="text-xl md:text-2xl text-slate-200 mb-8 leading-relaxed">
              Klimatisierte Lagerhallen in Deutschland – flexibel, sicher und versichert
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={scrollToContact}
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Gratis Offerte erhalten</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-slate-700/50 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-700 transition-all duration-300 border border-white/20 flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>0800 700 100</span>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <CheckCircle2 className="w-6 h-6 text-green-400 mb-2 mx-auto" />
                  <p className="text-sm font-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Was ist Zwischenlagerung?
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Bei einer Zwischenlagerung lagern wir Ihren kompletten Hausrat oder einzelne Möbelstücke temporär in unseren sicheren Lagerhallen ein – ideal wenn zwischen Auszug und Einzug eine zeitliche Lücke besteht. Ihre Möbel werden klimatisiert, überwacht und versichert gelagert, bis Ihre neue Wohnung bezugsbereit ist. Kombinierbar mit unserem <Link to="/komplettumzug" className="text-slate-900 font-semibold hover:underline">Komplettumzug Schweiz Deutschland</Link> oder <Link to="/teilumzug" className="text-slate-900 font-semibold hover:underline">Teilumzug</Link>.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                Alle Vorteile auf einen Blick:
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Unsere Lagerlösungen
              </h2>
              <p className="text-xl text-slate-600">
                Flexible Lagergrößen für jeden Bedarf
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {storageSizes.map((storage, index) => (
                <div
                  key={index}
                  className={`rounded-2xl p-8 border-2 transition-all duration-300 ${
                    storage.popular
                      ? 'bg-slate-900 border-slate-900 text-white scale-105'
                      : 'bg-white border-slate-200 hover:border-slate-800'
                  }`}
                >
                  {storage.popular && (
                    <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      BELIEBTESTE GRÖSSE
                    </div>
                  )}
                  <h3 className={`text-2xl font-bold mb-2 ${storage.popular ? 'text-white' : 'text-slate-900'}`}>
                    {storage.size}
                  </h3>
                  <p className={`text-sm mb-4 ${storage.popular ? 'text-slate-300' : 'text-slate-600'}`}>
                    {storage.ideal}
                  </p>
                  <div className={`text-3xl font-bold mb-6 ${storage.popular ? 'text-white' : 'text-slate-900'}`}>
                    {storage.price}
                  </div>
                  <div className={`border-t mb-6 ${storage.popular ? 'border-slate-700' : 'border-slate-200'}`} />
                  <ul className="space-y-3">
                    {storage.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            storage.popular ? 'text-green-400' : 'text-green-600'
                          }`}
                        />
                        <span className={`text-sm ${storage.popular ? 'text-slate-200' : 'text-slate-700'}`}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={scrollToContact}
                    className={`w-full mt-8 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      storage.popular
                        ? 'bg-white text-slate-900 hover:bg-slate-100'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    Offerte anfordern
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-slate-600">
                * Preise sind Richtwerte und können je nach Standort und Lagerdauer variieren. Transport und Auslieferung werden separat berechnet.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Maximale Sicherheit für Ihre Möbel
              </h2>
              <p className="text-xl text-slate-600">
                Modernste Sicherheitstechnik in allen Lagerhallen
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-lg">
                    <div className="bg-slate-800 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                Zusätzliche Sicherheitsmerkmale
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {safetyFeatures.map((safety, index) => (
                  <div key={index} className="text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <h4 className="font-bold text-slate-900 mb-1">{safety.title}</h4>
                    <p className="text-sm text-slate-600">{safety.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Wann brauche ich Zwischenlagerung?
              </h2>
              <p className="text-xl text-slate-600">
                Typische Situationen für temporäre Lagerung
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => {
                const Icon = useCase.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-8 border-2 border-slate-100">
                    <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-slate-800" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{useCase.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{useCase.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Warum Zwischenlagerung bei uns?
              </h2>
              <p className="text-xl text-slate-600">
                Ihr Partner für sichere und flexible Lagerung
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-100">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                So läuft die Zwischenlagerung ab
              </h2>
              <p className="text-xl text-slate-600">
                5 Schritte zur sicheren Lagerung
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-100">
                  <div className="bg-slate-900 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Das sagen unsere Kunden
              </h2>
              <p className="text-xl text-slate-600">
                Vertrauen Sie auf unsere Erfahrung
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-100">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Häufig gestellte Fragen
              </h2>
              <p className="text-xl text-slate-600">
                Alles über Zwischenlagerung bei Ihrem Umzug
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border-2 border-slate-100 overflow-hidden transition-all duration-300 hover:border-slate-300"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-600 transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
